// apps/client/src/pages/pipeline-tracker/tabs/TasksTab.tsx
//
// Pipeline Tracker: the Tasks tab. Per-person task compliance for one committed snapshot.
//
// Renders inside a page shell that already supplies `space-y-6` and the page padding, so this
// component adds neither.
//
// THREE THINGS THIS SCREEN DELIBERATELY DOES DIFFERENTLY FROM THE SPREADSHEET IT REPLACES:
//
//  1. `% completed` is rendered from an explicit zero-denominator rule. Where nothing was due it
//     says "no tasks due" rather than leaving a blank cell. The customer's own sheet leaves that
//     cell empty on 43 percent of rows and shows values like 9.999999999999999 on the rest,
//     because it divides by zero and prints a raw binary float.
//  2. Nothing is filtered by the deal snapshot. Most task rows have no matching deal, because
//     the Performance export is filtered and the Tasks export is not. The scope note says so on
//     screen, above the numbers, rather than in a footnote nobody reads.
//  3. Unclassified tasks get their own column. An export with a blank Progress and a blank Task
//     status is a real file; folding those rows into "to do" would invent obligations, and
//     dropping them would hide work.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlarmClock,
  AlertTriangle,
  CalendarClock,
  CalendarRange,
  ChevronDown,
  ChevronRight,
  CircleCheckBig,
  CircleDashed,
  ClipboardList,
  Gauge,
  Info,
  Inbox,
  ListTodo,
  RefreshCw,
  Tags,
} from 'lucide-react';
import { SkeletonStatCards, SkeletonTable } from '../../../components/common/SkeletonLoader';
import {
  completionRatio,
  fetchTaskDrilldown,
  formatCompletionPct,
  peekTaskDrilldown,
  prefetchTaskDrilldown,
  useTasksReport,
  type TaskAssigneeFigures,
  type TaskDrilldownRow,
} from '../../../hooks/pipelineTracker/useTasksReport';
import { groupTaskOwners, overduePct } from './taskOwnerGroups';
import { num } from './pipelineParts';
import { TrackerSection } from './TrackerSection';
import {
  AccentPanel, Chip, InfoStatCard, InfoTip, PeriodBar, RatioBar, ToneBanner, type ChipTone,
} from './trackerKit';
import { TaskDrawer } from './TaskDrawer';
import {
  TASK_PRIORITY_LABEL, defaultTaskFilter, filterTaskRows, taskSortLabel, type TaskDrawerFilter,
} from './taskDrawerFilters';

export interface TasksTabProps {
  /** Authorised tenant. The callable re-checks it; this is only which company to ask about. */
  companyId: string;
  /** Snapshot to read. Omitted means the most recently committed snapshot for the tenant. */
  asAtDate?: string;
}

interface ReportWindow {
  start: string;
  end: string;
}

/** Brand blue, per the design tokens. Used only for the section icons. */
const BRAND_BLUE = '#2563eb';

const CARD = 'rounded-lg border border-gray-700 bg-gray-800/40';

/** Headline tile colours, one per figure, so the six read apart at a glance. */
const TILE_COLOUR = {
  outstanding: '#2563eb',
  overdue: '#ef4444',
  dueInWindow: '#7c3aed',
  doneInWindow: '#16a34a',
  completed: '#0891b2',
  unclassified: '#f59e0b',
} as const;

const DRAWER_TITLE_ID = 'task-drawer-title';

/** How long the pointer or focus rests on a person's row before their task list is warmed. */
const PREFETCH_DELAY_MS = 150;

/** '2026-08-29' to '29 Aug 2026'. Parsed as UTC so a label cannot slip a day. */
function prettyDate(iso: string | null): string {
  if (!iso) return 'No due date';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

const iso = (d: Date): string => d.toISOString().slice(0, 10);

/** The period presets, as the house PeriodBar offers them on every SalesTrekker page. */
type TaskPeriodKey = 'wtd' | 'mtd' | 'qtd' | 'fytd' | 'cytd';

const PERIOD_LONG: Record<TaskPeriodKey, string> = {
  wtd: 'the week',
  mtd: 'the month',
  qtd: 'the financial quarter',
  fytd: 'the financial year',
  cytd: 'the calendar year',
};

/**
 * The due window for a preset: the WHOLE period containing the snapshot date, from its first day to
 * its LAST day (UTC, so no timezone can move it). A task window looks forward: a task due later in
 * the month is still a task due this month, which is why the default window has always been the
 * snapshot's calendar month to its end. MTD reproduces that default exactly; the other presets
 * apply the same rule to the week (Monday to Sunday), the financial quarter, the financial year
 * (1 July to 30 June) and the calendar year.
 */
export function taskPeriodWindow(key: TaskPeriodKey, anchor: string): ReportWindow {
  const [y, m, d] = anchor.split('-').map(Number);
  const mi = m - 1;
  switch (key) {
    case 'wtd': {
      const day = new Date(Date.UTC(y, mi, d));
      const dow = day.getUTCDay();
      const back = dow === 0 ? 6 : dow - 1;
      return { start: iso(new Date(Date.UTC(y, mi, d - back))), end: iso(new Date(Date.UTC(y, mi, d - back + 6))) };
    }
    case 'mtd':
      return { start: iso(new Date(Date.UTC(y, mi, 1))), end: iso(new Date(Date.UTC(y, mi + 1, 0))) };
    case 'qtd': {
      const qStart = mi - (((mi - 6 + 12) % 12) % 3);
      return { start: iso(new Date(Date.UTC(y, qStart, 1))), end: iso(new Date(Date.UTC(y, qStart + 3, 0))) };
    }
    case 'fytd': {
      const fy = mi >= 6 ? y : y - 1;
      return { start: `${fy}-07-01`, end: `${fy + 1}-06-30` };
    }
    case 'cytd':
    default:
      return { start: `${y}-01-01`, end: `${y}-12-31` };
  }
}

/**
 * The completion tone. The thresholds are the ones this cell has always used; only the
 * presentation (a pill with a bar) is new.
 */
function completionTone(ratio: number): { chip: ChipTone; bar: string } {
  if (ratio >= 0.9) return { chip: 'green', bar: '#22c55e' };
  if (ratio >= 0.6) return { chip: 'amber', bar: '#f59e0b' };
  return { chip: 'red', bar: '#ef4444' };
}

/**
 * The completion cell.
 *
 * A zero denominator is a sentence, not a blank. "0%" would be a lie: it says the person
 * completed none of what was due, when in fact nothing was due.
 */
const CompletionCell: React.FC<{ figures: TaskAssigneeFigures }> = ({ figures }) => {
  const ratio = completionRatio(figures.doneInWindow, figures.totalDueInWindow);
  if (ratio === null) {
    return <span className="text-xs text-gray-500">No tasks due</span>;
  }
  const tone = completionTone(ratio);
  return (
    <span className="inline-flex min-w-[4.5rem] flex-col items-end gap-1">
      <Chip tone={tone.chip}>{formatCompletionPct(ratio)}</Chip>
      <RatioBar ratio={ratio} color={tone.bar} className="w-full" />
    </span>
  );
};

const StateBadge: React.FC<{ state: TaskDrilldownRow['state'] }> = ({ state }) => {
  const label =
    state === 'todo' ? 'To do' : state === 'doing' ? 'Doing' : state === 'done' ? 'Done' : 'Not classified';
  const tone =
    state === 'done'
      ? 'border-green-900/60 bg-green-950/40 text-green-300'
      : state === 'doing'
        ? 'border-blue-900/60 bg-blue-950/40 text-blue-300'
        : state === 'todo'
          ? 'border-gray-600 bg-gray-800 text-gray-300'
          : 'border-amber-900/60 bg-amber-950/40 text-amber-300';
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${tone}`}>
      {label}
    </span>
  );
};

/**
 * A task's priority as Salestrekker set it. High reads red because it is the figure the brief asks
 * about; "Not set" is muted and is a statement about the export, not about the work. A row from a
 * payload that predates priority carries none and says so rather than guessing.
 */
const PriorityBadge: React.FC<{ priority: TaskDrilldownRow['priority'] }> = ({ priority }) => {
  if (priority === undefined) return <span className="text-gray-500">n/a</span>;
  const tone =
    priority === 'high'
      ? 'border-red-900/60 bg-red-950/40 text-red-300'
      : priority === 'medium'
        ? 'border-amber-900/60 bg-amber-950/40 text-amber-300'
        : priority === 'low'
          ? 'border-gray-600 bg-gray-800 text-gray-300'
          : 'border-gray-700 bg-gray-800/60 text-gray-500';
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${tone}`}>
      {TASK_PRIORITY_LABEL[priority]}
    </span>
  );
};

const CELL = 'px-3 py-2.5 text-right tabular-nums';

/**
 * One row's figure cells, shared by each person, each subtotal and the All row, so the three kinds
 * of row cannot drift into different column orders.
 *
 * Outstanding splits into Not due, Due and Overdue (V6). Overdue % is overdue over outstanding, and
 * reads "n/a" when nothing is outstanding rather than a 0% that would claim a clean record.
 * Overdue, high priority sits beside Overdue: of those tasks, the ones Salestrekker marks High.
 */
const FigureCells: React.FC<{ figures: TaskAssigneeFigures; hasSplit: boolean; hasPriority: boolean }> = ({
  figures, hasSplit, hasPriority,
}) => {
  const pct = overduePct(figures);
  return (
    <>
      <td className={CELL}>{figures.toDo}</td>
      <td className={CELL}>{figures.doing}</td>
      <td className={CELL}>{figures.done}</td>
      <td className={`${CELL} text-gray-400`}>{figures.unclassified}</td>
      <td className={CELL}>{figures.totalDue}</td>
      <td className={`${CELL} text-blue-200`}>{figures.outstanding}</td>
      {hasSplit && <td className={`${CELL} text-gray-400`}>{figures.notDue ?? 0}</td>}
      {hasSplit && <td className={`${CELL} text-violet-200`}>{figures.due ?? 0}</td>}
      <td className={CELL}>
        {figures.totalOverdue > 0 ? <Chip tone="amber">{figures.totalOverdue}</Chip> : figures.totalOverdue}
      </td>
      {hasPriority && (
        <td className={CELL} data-testid="overdue-high-cell">
          {(figures.overdueHigh ?? 0) > 0 ? <Chip tone="red">{figures.overdueHigh}</Chip> : figures.overdueHigh ?? 0}
        </td>
      )}
      {hasSplit && (
        <td className={`${CELL} text-gray-300`}>{pct === null ? 'n/a' : `${pct}%`}</td>
      )}
      <td className={CELL}>{figures.doneInWindow}</td>
      <td className={CELL}>{figures.totalDueInWindow}</td>
      <td className="px-3 py-2.5 text-right">
        <CompletionCell figures={figures} />
      </td>
    </>
  );
};

const TH = 'sticky top-0 z-10 bg-gray-900 px-3 py-2.5 font-semibold';

/** Unmapped task names listed before "Show all". */
export const UNMAPPED_SHOWN = 15;

/** Active filter chip tones; an idle chip is muted with its figure in the tone's colour. */
const FILTER_TONE: Record<'blue' | 'amber' | 'red' | 'violet' | 'green' | 'muted', { active: string; figure: string }> = {
  blue: { active: 'border-blue-400/70 bg-blue-500/25 text-blue-100 ring-1 ring-blue-400/60', figure: 'text-blue-200' },
  amber: { active: 'border-amber-400/70 bg-amber-500/25 text-amber-100 ring-1 ring-amber-400/60', figure: 'text-amber-200' },
  red: { active: 'border-red-400/70 bg-red-500/25 text-red-100 ring-1 ring-red-400/60', figure: 'text-red-200' },
  violet: { active: 'border-violet-400/70 bg-violet-500/25 text-violet-100 ring-1 ring-violet-400/60', figure: 'text-violet-200' },
  green: { active: 'border-emerald-400/70 bg-emerald-500/25 text-emerald-100 ring-1 ring-emerald-400/60', figure: 'text-emerald-200' },
  muted: { active: 'border-gray-400/70 bg-white/[0.12] text-white ring-1 ring-gray-400/60', figure: 'text-gray-200' },
};

/** One drawer figure as a toggle: pressed, it filters the list to the tasks it counts. */
const FilterChip: React.FC<{
  label: string;
  value: string;
  tone: keyof typeof FILTER_TONE;
  pressed: boolean;
  onClick: () => void;
}> = ({ label, value, tone, pressed, onClick }) => (
  <button
    type="button"
    aria-pressed={pressed}
    onClick={onClick}
    className={`inline-flex items-baseline gap-1.5 rounded-lg border px-2.5 py-1 text-xs transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
      pressed ? FILTER_TONE[tone].active : 'border-gray-700 bg-white/[0.04] text-gray-400 hover:border-gray-500 hover:bg-blue-500/[0.06] hover:text-gray-200'
    }`}
  >
    <span className="font-medium uppercase tracking-wide opacity-80">{label}</span>
    <span className={`text-sm font-bold tabular-nums ${pressed ? '' : FILTER_TONE[tone].figure}`}>{value}</span>
  </button>
);

const FILTER_NOUN: Record<TaskDrawerFilter, string> = {
  all: 'tasks',
  outstanding: 'outstanding tasks',
  overdue: 'overdue tasks',
  overdueHigh: 'overdue, high priority tasks',
  dueInWindow: 'tasks due in the window',
  doneInWindow: 'tasks done in the window',
};

/**
 * A task's age. Amber only for open work past its due date: a done task's age is history, not a
 * warning, so it reads muted with what it means. A due date after the as-at date gives a negative
 * age, which reads as nonsense ("-12 days old"), so it says what it means instead: not due yet,
 * or for a done task, done before it fell due.
 */
const AgeCell: React.FC<{ row: TaskDrilldownRow }> = ({ row }) => {
  if (row.ageDays === null) return <span className="text-gray-500">n/a</span>;
  const open = row.state === 'todo' || row.state === 'doing';
  if (row.ageDays < 0) {
    const days = `${Math.abs(row.ageDays)} day${row.ageDays === -1 ? '' : 's'}`;
    return row.state === 'done' ? (
      <span className="text-gray-500" title={`Done. Due ${days} after the as-at date.`}>Before due</span>
    ) : (
      <span className="text-gray-500" title={`Due ${days} after the as-at date.`}>Not due</span>
    );
  }
  if (open && row.ageDays > 0) return <Chip tone="amber" className="tabular-nums">{row.ageDays}</Chip>;
  if (row.state === 'done') {
    return <span className="text-gray-500" title="Done. Days from the due date to the as-at date.">{row.ageDays}</span>;
  }
  return <span className="text-gray-400">{row.ageDays}</span>;
};

const TasksTab: React.FC<TasksTabProps> = ({ companyId, asAtDate }) => {
  const [reportWindow, setReportWindow] = useState<ReportWindow | null>(null);
  // MTD is the server's default window (the snapshot month to its end), so it sends no window at
  // all: choosing it costs no second scan.
  const [periodKey, setPeriodKey] = useState<TaskPeriodKey | 'custom'>('mtd');

  const { response, loading, error, refetch } = useTasksReport({
    companyId,
    asAtDate,
    windowStart: reportWindow?.start,
    windowEnd: reportWindow?.end,
  });

  const report = response && response.status === 'ok' ? response : null;

  // The picker DISPLAYS the window the server chose (the snapshot's calendar month) until the
  // reader changes it, and only a deliberate change sends one. Adopting the server's window into
  // state on arrival would look identical and cost a second BigQuery scan on every tab open, for
  // a request whose answer we already have.

  const [openAssignee, setOpenAssignee] = useState<string | null>(null);
  // Each person's row, so closing the drawer can hand focus back to the row that opened it.
  const rowRefs = useRef(new Map<string, HTMLTableRowElement>());
  const [drilldown, setDrilldown] = useState<{ rows: TaskDrilldownRow[]; total: number } | null>(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownError, setDrilldownError] = useState<string | null>(null);
  // 'auto' until the reader picks a chip: the most actionable view for the list that arrived.
  const [drawerFilter, setDrawerFilter] = useState<TaskDrawerFilter | 'auto'>('auto');
  // The server's own list for the "Overdue, high priority" chip. The drill-down is bounded (500
  // rows, most overdue first), so narrowing the held list in the browser could drop high priority
  // tasks past the bound on a heavy book while the chip's figure, from the totals, still counted
  // them. The server narrows before it bounds. Null until it answers; the held list, narrowed here,
  // stands in meanwhile and is never wider than the answer.
  const [highDrilldown, setHighDrilldown] = useState<{ rows: TaskDrilldownRow[]; total: number } | null>(null);
  const [showAllUnmapped, setShowAllUnmapped] = useState(false);

  const drilldownParams = useCallback(
    (assignee: string) => ({
      companyId,
      assignee,
      ...(asAtDate ? { asAtDate } : {}),
      ...(reportWindow ? { windowStart: reportWindow.start, windowEnd: reportWindow.end } : {}),
    }),
    [companyId, asAtDate, reportWindow]
  );

  // The latest open, so a slow answer for a person already closed (or swapped for another) is
  // never drawn under the wrong name.
  const openSeq = useRef(0);
  const openDrilldown = useCallback(
    async (assignee: string) => {
      const seq = ++openSeq.current;
      const params = drilldownParams(assignee);
      // A list already held (prefetched on hover, or opened before) is drawn at once, with no
      // skeleton; fetchTaskDrilldown below then answers from the cache or re-asks an aged one.
      const cached = peekTaskDrilldown(params);
      setOpenAssignee(assignee);
      setDrilldown(cached ?? null);
      setDrawerFilter('auto');
      setDrilldownError(null);
      setDrilldownLoading(!cached);
      try {
        const result = await fetchTaskDrilldown(params);
        if (seq === openSeq.current) setDrilldown(result);
      } catch (err) {
        console.error('[TasksTab] Failed to load the task drill-down:', err);
        if (seq === openSeq.current && !cached) {
          setDrilldownError(
            err instanceof Error ? err.message : 'Failed to load the tasks for this person'
          );
        }
      } finally {
        if (seq === openSeq.current) setDrilldownLoading(false);
      }
    },
    [drilldownParams]
  );

  // Warm the drill-down for the row the reader rests on, so the click usually finds it ready.
  // The short delay means sweeping the pointer (or tabbing) across the table does not ask for
  // every person on the way: only a row rested on is fetched.
  const prefetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelPrefetch = useCallback(() => {
    if (prefetchTimer.current !== null) clearTimeout(prefetchTimer.current);
    prefetchTimer.current = null;
  }, []);
  const schedulePrefetch = useCallback(
    (assignee: string) => {
      cancelPrefetch();
      prefetchTimer.current = setTimeout(() => {
        prefetchTimer.current = null;
        prefetchTaskDrilldown(drilldownParams(assignee));
      }, PREFETCH_DELAY_MS);
    },
    [cancelPrefetch, drilldownParams]
  );
  useEffect(() => cancelPrefetch, [cancelPrefetch]);

  useEffect(() => {
    if (openAssignee === null || drawerFilter !== 'overdueHigh') {
      setHighDrilldown(null);
      return;
    }
    let cancelled = false;
    const params = { ...drilldownParams(openAssignee), priority: 'high' as const };
    const cached = peekTaskDrilldown(params);
    if (cached) setHighDrilldown(cached);
    fetchTaskDrilldown(params)
      .then((result) => {
        if (!cancelled) setHighDrilldown(result);
      })
      .catch((err) => {
        // The held list, narrowed in the browser, stays on screen; the failure is logged, not hidden.
        console.error('[TasksTab] Failed to load the high priority task drill-down:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [openAssignee, drawerFilter, drilldownParams]);

  const closeDrilldown = useCallback(() => {
    const row = openAssignee === null ? undefined : rowRefs.current.get(openAssignee);
    setOpenAssignee(null);
    row?.focus();
  }, [openAssignee]);

  const selectPeriod = useCallback(
    (key: string) => {
      if (!report) return;
      if (key === 'custom') {
        setPeriodKey('custom');
        return;
      }
      const k = key as TaskPeriodKey;
      setPeriodKey(k);
      setReportWindow(k === 'mtd' ? null : taskPeriodWindow(k, report.provenance.asAtDate));
    },
    [report]
  );

  const hasSplit = report !== null && report.totals.due !== undefined && report.totals.notDue !== undefined;
  // A payload from before priority was read has no overdueHigh; the column, chip and note are
  // withheld rather than shown as zero, which would claim nothing urgent is late.
  const hasPriority = report !== null && report.totals.overdueHigh !== undefined;
  const ownerGroups = report ? groupTaskOwners(report) : null;
  // Assignee, ten figure cells (thirteen with the outstanding split, one more with priority), and
  // the drill-down cue.
  const columnCount = (hasSplit ? 15 : 12) + (hasPriority ? 1 : 0);
  const openFigures = report && openAssignee !== null
    ? report.assignees.find((a) => a.assignee === openAssignee) ?? null
    : null;

  const renderPersonRow = (person: TaskAssigneeFigures) => (
    <tr
      key={person.assignee}
      ref={(el) => {
        if (el) rowRefs.current.set(person.assignee, el);
        else rowRefs.current.delete(person.assignee);
      }}
      // The whole row opens the drill-down. A single small
      // button at the far right of a wide table is a target
      // most people miss, and every cell in the row is about
      // this person's tasks.
      onClick={() => {
        cancelPrefetch();
        openDrilldown(person.assignee);
      }}
      onMouseEnter={() => schedulePrefetch(person.assignee)}
      onMouseLeave={cancelPrefetch}
      onFocus={() => schedulePrefetch(person.assignee)}
      onBlur={cancelPrefetch}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          cancelPrefetch();
          openDrilldown(person.assignee);
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`Show tasks for ${person.assignee}`}
      aria-haspopup="dialog"
      className={`group cursor-pointer border-t border-gray-700/50 transition-colors odd:bg-white/[0.015] hover:bg-blue-500/[0.07] focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-blue-500 ${
        openAssignee === person.assignee ? 'bg-blue-500/10' : ''
      }`}
    >
      <td className="px-3 py-2.5">
        <span className={person.isUnassigned ? 'italic text-gray-400' : 'font-medium text-gray-100'}>
          {person.assignee}
        </span>
      </td>
      <FigureCells figures={person} hasSplit={hasSplit} hasPriority={hasPriority} />
      <td className="px-3 py-2.5 text-right">
        {/* A visual cue, not a control. The ROW is the button:
            nesting an interactive element inside one is both
            an accessibility fault and a double fetch. */}
        <span
          aria-hidden="true"
          className="inline-flex items-center gap-1 rounded-md border border-blue-500/30 bg-blue-500/10 px-2 py-1 text-xs font-semibold text-blue-200 transition-colors group-hover:border-blue-400/60 group-hover:bg-blue-500/20"
        >
          Tasks
          <ChevronRight className="h-3.5 w-3.5" />
        </span>
      </td>
    </tr>
  );

  if (!companyId) {
    return (
      <div className={`${CARD} px-5 py-10 text-center`}>
        <Inbox className="mx-auto h-8 w-8 text-gray-500" aria-hidden="true" />
        <p className="mt-3 text-sm text-gray-400">No company is selected.</p>
      </div>
    );
  }

  const effectiveWindow = report ? (reportWindow ?? report.provenance.window) : null;
  // Plain computations, not hooks: this sits after the no-company early return. At most 500 rows.
  const drawerScope = report && effectiveWindow ? { asAtDate: report.provenance.asAtDate, window: effectiveWindow } : null;
  const activeFilter: TaskDrawerFilter = drawerFilter !== 'auto'
    ? drawerFilter
    : drilldown && drawerScope ? defaultTaskFilter(drilldown.rows, drawerScope) : 'all';
  const shownRows = activeFilter === 'overdueHigh' && highDrilldown !== null ? highDrilldown.rows : drilldown?.rows ?? [];
  const shownTasks = drilldown && drawerScope ? filterTaskRows(shownRows, activeFilter, drawerScope) : [];
  const toggleFilter = (f: TaskDrawerFilter) => setDrawerFilter(activeFilter === f ? 'all' : f);
  const completedRatio = report
    ? completionRatio(report.totals.doneInWindow, report.totals.totalDueInWindow)
    : null;

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-lg border border-red-900/60 bg-red-950/30 p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 shrink-0 text-red-400 mt-0.5" aria-hidden="true" />
          <div className="flex-1 text-sm text-red-300">
            <p>{error}</p>
            <button
              type="button"
              onClick={refetch}
              className="mt-2 inline-flex items-center gap-2 rounded-md border border-red-800 px-3 py-1.5 text-xs text-red-200 hover:bg-red-900/40"
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              Try again
            </button>
          </div>
        </div>
      )}

      {response && response.status === 'invalid_request' && (
        <div className="rounded-lg border border-amber-900/60 bg-amber-950/30 p-4 text-sm text-amber-300">
          That reporting window could not be used: {response.reason}
        </div>
      )}

      {response && response.status === 'no_snapshot' && (
        <div className={`${CARD} px-5 py-10 text-center`}>
          <ClipboardList className="mx-auto h-8 w-8 text-gray-500" aria-hidden="true" />
          <p className="mt-3 text-sm text-gray-300">
            {response.reason === 'never_ingested'
              ? 'No pipeline snapshot has been uploaded yet.'
              : 'No snapshot has been published for that date yet.'}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Upload a Salestrekker Tasks export to start tracking task compliance.
          </p>
        </div>
      )}

      {loading && !report && (
        <div className="space-y-6">
          <SkeletonStatCards count={4} columns={4} />
          <SkeletonTable rows={8} columns={6} />
        </div>
      )}

      {report && effectiveWindow && (
        <TrackerSection
          id="ops-tasks"
          step="1 · Tasks"
          title="Who has tasks outstanding or overdue"
          description={`Every task in the snapshot is counted; most task rows have no matching deal by design. Overdue is measured against ${prettyDate(report.provenance.asAtDate)}, so the figures do not drift.`}
        >
          {/* Scope note first, before any number. Without it, a reader takes these counts for the
              whole book of work attached to live deals, which is not what they are. */}
          <ToneBanner icon={Info} color={BRAND_BLUE}>
            <p className="text-blue-50">{report.provenance.scopeNote}</p>
            <p className="mt-2 text-xs text-blue-200/80">
              Snapshot as at {prettyDate(report.provenance.asAtDate)}. Overdue is measured
              against that date, not against today, so the figures do not drift between readings.
            </p>
          </ToneBanner>

          {/* The house period selector, as on every SalesTrekker page. Each preset is the whole
              period containing the snapshot, to its last day; the chip states the window in dates. */}
          <div className="rounded-xl border border-gray-700/70 bg-gradient-to-r from-blue-500/[0.08] via-gray-800/40 to-gray-800/40 px-4 py-3">
            <PeriodBar
              selected={periodKey}
              onSelect={selectPeriod}
              custom={reportWindow ? { from: reportWindow.start, to: reportWindow.end } : undefined}
              onApplyCustom={(from, to) => {
                setPeriodKey('custom');
                setReportWindow({ start: from, end: to });
              }}
              ariaLabel="Task due window"
              trailing={
                <>
                  <span data-testid="task-window-chip" className="inline-flex items-center gap-1.5">
                    <Chip tone="blue">
                      <CalendarRange className="h-3.5 w-3.5" aria-hidden="true" />
                      Due {prettyDate(effectiveWindow.start)} to {prettyDate(effectiveWindow.end)}
                    </Chip>
                    <InfoTip label="the task due window">
                      {periodKey === 'custom'
                        ? 'Tasks due between the two dates you applied, both included.'
                        : `Tasks due in ${PERIOD_LONG[periodKey]} containing the snapshot date, from its first day to its last. A task still due later in the period counts as due in it.`}{' '}
                      Overdue and outstanding are measured as at {prettyDate(report.provenance.asAtDate)}.
                    </InfoTip>
                  </span>
                  {loading && <span className="text-xs text-blue-300">Updating…</span>}
                </>
              }
            />
          </div>

          {/* Headline figures, two rows of three. These are the totals row of the table below, so
              the two can never disagree: both come from the same payload. Each card carries a short
              sub-line and its full explanation in a tooltip, so all six are the same height.
              Not classified is a tile of its own, carrying the explanation the separate banner
              used to (founder, 5 Oct 2026). */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="task-headline-tiles">
            <InfoStatCard
              label="Outstanding"
              value={num(report.totals.outstanding)}
              sub="To do plus doing"
              info="To do plus doing, across everyone."
              icon={ListTodo}
              color={TILE_COLOUR.outstanding}
            />
            <InfoStatCard
              label="Overdue"
              value={num(report.totals.totalOverdue)}
              sub="Past the as-at date"
              info={`Open and past the as-at date (${prettyDate(report.provenance.asAtDate)}).`}
              icon={AlarmClock}
              color={TILE_COLOUR.overdue}
              tone={report.totals.totalOverdue > 0 ? 'warn' : 'plain'}
            />
            <InfoStatCard
              label="Due in window"
              value={num(report.totals.totalDueInWindow)}
              sub="In the chosen window"
              info={`Tasks falling due from ${prettyDate(report.provenance.window.start)} to ${prettyDate(report.provenance.window.end)}.`}
              icon={CalendarClock}
              color={TILE_COLOUR.dueInWindow}
            />
            <InfoStatCard
              label="Done in window"
              value={num(report.totals.doneInWindow)}
              sub="Of tasks due in window"
              info="Of the tasks due in the window, the number marked done."
              icon={CircleCheckBig}
              color={TILE_COLOUR.doneInWindow}
            />
            <InfoStatCard
              label="Completed"
              value={formatCompletionPct(completedRatio)}
              sub="Done over due"
              info="Done in window over total due in window."
              icon={Gauge}
              color={TILE_COLOUR.completed}
            />
            <InfoStatCard
              label="Not classified"
              value={num(report.totals.unclassified)}
              sub="Blank progress and status"
              info={report.provenance.unclassifiedNote}
              icon={CircleDashed}
              color={TILE_COLOUR.unclassified}
              tone={report.totals.unclassified > 0 ? 'warn' : 'plain'}
            />
          </div>

          {/* Per-person table. */}
          <AccentPanel
            title="Task compliance by person"
            icon={ClipboardList}
            color={BRAND_BLUE}
            flush
            blurb={(hasSplit || report.provenance.ownerClassification) ? (
              <div className="space-y-1">
                {hasSplit && (
                  <p>
                    Outstanding splits three ways as at {prettyDate(report.provenance.asAtDate)}:
                    Overdue is past its due date, Due falls due by{' '}
                    {prettyDate(report.provenance.window.end)} (the end of the window above), and Not
                    due is later or has no due date.
                  </p>
                )}
                {report.provenance.ownerClassification?.status === 'classified' && (
                  <p>{report.provenance.ownerClassification.note}</p>
                )}
                {report.provenance.ownerClassification?.status === 'unavailable' && (
                  <p className="text-amber-300">{report.provenance.ownerClassification.reason}</p>
                )}
              </div>
            ) : undefined}
          >
            {report.assignees.length === 0 ? (
              <div className="px-5 py-10 text-center">
                <Inbox className="mx-auto h-8 w-8 text-gray-500" aria-hidden="true" />
                <p className="mt-3 text-sm text-gray-400">
                  This snapshot contains no tasks.
                </p>
              </div>
            ) : (
              <div className="max-h-[70vh] overflow-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[0.6875rem] uppercase tracking-wider text-gray-400">
                      <th className={TH}>Assignee</th>
                      <th className={`${TH} text-right`}>To do</th>
                      <th className={`${TH} text-right`}>Doing</th>
                      <th className={`${TH} text-right`}>Done</th>
                      <th className={`${TH} text-right`}>Not classified</th>
                      <th className={`${TH} text-right`}>Total due</th>
                      <th className={`${TH} text-right`}>Outstanding</th>
                      {hasSplit && <th className={`${TH} text-right`}>Not due</th>}
                      {hasSplit && <th className={`${TH} text-right`}>Due</th>}
                      <th className={`${TH} text-right`}>Overdue</th>
                      {hasPriority && <th className={`${TH} text-right`}>Overdue, high priority</th>}
                      {hasSplit && <th className={`${TH} text-right`}>Overdue %</th>}
                      <th className={`${TH} text-right`}>Done in window</th>
                      <th className={`${TH} text-right`}>Due in window</th>
                      <th className={`${TH} text-right`}>Completed</th>
                      <th className={TH} aria-label="Open the task list" />
                    </tr>
                  </thead>
                  <tbody>
                    {ownerGroups === null
                      ? report.assignees.map(renderPersonRow)
                      : ownerGroups.map((group) => {
                          const dot = group.key === 'broker' ? 'bg-blue-500' : 'bg-violet-500';
                          return (
                            <React.Fragment key={group.key}>
                              <tr className="border-t border-gray-700/60">
                                <td
                                  colSpan={columnCount}
                                  className="px-3 pb-1.5 pt-4 text-[0.6875rem] font-bold uppercase tracking-[0.08em] text-gray-300"
                                >
                                  <span aria-hidden="true" className={`mr-2 inline-block h-2 w-2 rounded-full align-middle ${dot}`} />
                                  {group.label}
                                </td>
                              </tr>
                              {group.people.length === 0 ? (
                                <tr>
                                  <td colSpan={columnCount} className="px-3 py-2 text-xs text-gray-500">
                                    No {group.key === 'broker' ? 'broker' : 'back-office owner'} holds a task in this snapshot.
                                  </td>
                                </tr>
                              ) : (
                                group.people.map(renderPersonRow)
                              )}
                              <tr className="border-t border-gray-600 bg-blue-500/[0.05] font-semibold">
                                <td className="px-3 py-2.5">{group.label} subtotal</td>
                                <FigureCells figures={group.subtotal} hasSplit={hasSplit} hasPriority={hasPriority} />
                                <td />
                              </tr>
                            </React.Fragment>
                          );
                        })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-blue-500/40 bg-blue-500/[0.08] font-semibold text-gray-100">
                      <td className="px-3 py-2.5">All</td>
                      <FigureCells figures={report.totals} hasSplit={hasSplit} hasPriority={hasPriority} />
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
            {hasPriority && report.assignees.length > 0 && (
              <p className="border-t border-gray-700/60 px-5 py-3 text-xs text-gray-400" data-testid="overdue-high-note">
                Overdue, high priority is based on the snapshot date ({prettyDate(report.provenance.asAtDate)}) and
                Salestrekker's task priority. Tasks without a priority are counted as "not set" and are never
                counted as high priority.
              </p>
            )}
          </AccentPanel>

          {/* Mapping worklist. An unmapped task name still counts towards every figure above; it
              simply has no canonical group yet. Surfacing it with a count is what makes it
              fixable, and a mapping correction retroactively fixes historic snapshots. */}
          {report.unmappedTaskNames.length > 0 && (
            <AccentPanel
              title="Task names that need mapping"
              icon={Tags}
              color="#f59e0b"
              badge={<Chip tone="amber">{report.taskNameCoverage.coveragePct}% mapped</Chip>}
            >
              <p className="text-sm text-gray-400">
                {report.taskNameCoverage.coveragePct}% of tasks resolve to a task group. These
                names are counted in every figure above; they simply have no group yet. Mapping
                one also fixes every snapshot already captured.
              </p>
              <RatioBar
                ratio={report.taskNameCoverage.coveragePct / 100}
                color="#f59e0b"
                className="mt-3"
              />
              <ul className="mt-3 divide-y divide-gray-700/50">
                {(showAllUnmapped ? report.unmappedTaskNames : report.unmappedTaskNames.slice(0, UNMAPPED_SHOWN)).map((item) => (
                  <li
                    key={`${item.workflow}:${item.normalisedRaw}`}
                    className="flex items-center justify-between gap-4 py-1.5 text-sm"
                  >
                    <span className="text-gray-300">{item.raw}</span>
                    <Chip tone="amber">{item.count}</Chip>
                  </li>
                ))}
              </ul>
              {report.unmappedTaskNames.length > UNMAPPED_SHOWN && (
                <div className="mt-3 border-t border-gray-700/60">
                  <button
                    type="button"
                    onClick={() => setShowAllUnmapped((v) => !v)}
                    aria-expanded={showAllUnmapped}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-2.5 text-left text-sm text-gray-200 transition-colors hover:bg-blue-500/[0.06] hover:text-white"
                  >
                    {showAllUnmapped
                      ? <ChevronDown className="h-4 w-4" aria-hidden="true" />
                      : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
                    {showAllUnmapped
                      ? 'Show fewer'
                      : `Show all ${num(report.unmappedTaskNames.length)} task names`}
                    <span className="ml-auto">
                      <Chip tone="amber" className="tabular-nums">{num(report.unmappedTaskNames.length)}</Chip>
                    </span>
                  </button>
                </div>
              )}
            </AccentPanel>
          )}

          {report.provenance.taxonomyInvalidReason && (
            <div className="rounded-lg border border-amber-900/60 bg-amber-950/20 p-4 text-sm text-amber-300">
              The saved task-name mapping could not be read and the defaults were used instead, so
              these groups may not match what was configured. Reason:{' '}
              {report.provenance.taxonomyInvalidReason}
            </div>
          )}
        </TrackerSection>
      )}

      {/* Drill-down, as a slide-out panel. Deal reference, task group, due date and age. Nothing
          else exists in the snapshot: there is no borrower name, no deal name and no comments
          column. */}
      <TaskDrawer
        open={openAssignee !== null}
        onClose={closeDrilldown}
        titleId={DRAWER_TITLE_ID}
        header={
          <>
            <h2 id={DRAWER_TITLE_ID} className="leading-tight">
              <span className="block text-[0.6875rem] font-bold uppercase tracking-[0.1em] text-blue-400">Tasks for</span>{' '}
              <span className="mt-0.5 block text-lg font-bold text-white">{openAssignee}</span>
            </h2>
            {effectiveWindow && (
              <p className="mt-1 text-xs text-gray-400">
                Due in window {prettyDate(effectiveWindow.start)} to {prettyDate(effectiveWindow.end)}
              </p>
            )}
            {openFigures && (
              <div
                className="mt-3 flex flex-wrap items-center gap-2"
                data-testid="task-drawer-figures"
                role="group"
                aria-label="Filter the task list"
              >
                <FilterChip label="All" value={num(openFigures.totalTasks)} tone="muted" pressed={activeFilter === 'all'} onClick={() => setDrawerFilter('all')} />
                <FilterChip label="Outstanding" value={num(openFigures.outstanding)} tone="blue" pressed={activeFilter === 'outstanding'} onClick={() => toggleFilter('outstanding')} />
                <FilterChip label="Overdue" value={num(openFigures.totalOverdue)} tone="amber" pressed={activeFilter === 'overdue'} onClick={() => toggleFilter('overdue')} />
                {openFigures.overdueHigh !== undefined && (
                  <FilterChip label="Overdue, high priority" value={num(openFigures.overdueHigh)} tone="red" pressed={activeFilter === 'overdueHigh'} onClick={() => toggleFilter('overdueHigh')} />
                )}
                <FilterChip label="Due in window" value={num(openFigures.totalDueInWindow)} tone="violet" pressed={activeFilter === 'dueInWindow'} onClick={() => toggleFilter('dueInWindow')} />
                <FilterChip label="Done in window" value={num(openFigures.doneInWindow)} tone="green" pressed={activeFilter === 'doneInWindow'} onClick={() => toggleFilter('doneInWindow')} />
                {/* A ratio of two chips, not a set of tasks: a stat, not a filter. */}
                <span
                  className="ml-1 inline-flex items-baseline gap-1.5 border-l border-gray-700 pl-3 text-xs text-cyan-200"
                  data-testid="task-drawer-completed"
                >
                  <span className="font-medium uppercase tracking-wide opacity-80">Completed</span>
                  <span className="text-sm font-bold tabular-nums">
                    {(() => {
                      const ratio = completionRatio(openFigures.doneInWindow, openFigures.totalDueInWindow);
                      return ratio === null ? 'No tasks due' : formatCompletionPct(ratio);
                    })()}
                  </span>
                </span>
              </div>
            )}
          </>
        }
      >
        {drilldownLoading && (
          <div className="p-5">
            <SkeletonTable rows={6} columns={5} />
          </div>
        )}

        {drilldownError && (
          <div className="m-5 rounded-lg border border-red-900/60 bg-red-950/30 p-4 text-sm text-red-300">
            {drilldownError}
          </div>
        )}

        {!drilldownLoading && !drilldownError && drilldown && drilldown.rows.length === 0 && (
          <div className="px-5 py-10 text-center">
            <Inbox className="mx-auto h-8 w-8 text-gray-500" aria-hidden="true" />
            <p className="mt-3 text-sm text-gray-400">
              This person has no tasks in the snapshot.
            </p>
          </div>
        )}

        {!drilldownLoading && !drilldownError && drilldown && drilldown.rows.length > 0 && (
          <div className="pb-5">
            <p className="px-5 pb-1 pt-3 text-xs text-gray-400" data-testid="task-drawer-count" aria-live="polite">
              {num(shownTasks.length)} {FILTER_NOUN[activeFilter]}
              , {taskSortLabel(activeFilter)}
            </p>
            {shownTasks.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-gray-400">No {FILTER_NOUN[activeFilter]} for this person.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[0.6875rem] uppercase tracking-wider text-gray-400">
                    <th className={TH}>Deal reference</th>
                    <th className={TH}>Task group</th>
                    <th className={`${TH} whitespace-nowrap`}>Due date</th>
                    <th className={`${TH} whitespace-nowrap text-right`}>Age (days)</th>
                    <th className={TH}>State</th>
                    {hasPriority && <th className={TH}>Priority</th>}
                  </tr>
                </thead>
                <tbody>
                  {shownTasks.map((row) => (
                    <tr key={row.taskId} className="border-t border-gray-700/50 odd:bg-white/[0.015] transition-colors hover:bg-blue-500/[0.06]">
                      <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs text-gray-300">
                        {row.dealRef ?? 'No deal reference'}
                      </td>
                      <td className="px-3 py-2.5 text-gray-200">{row.taskGroup}</td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-gray-300">{prettyDate(row.dueDate)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">
                        <AgeCell row={row} />
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <StateBadge state={row.state} />
                      </td>
                      {hasPriority && (
                        <td className="whitespace-nowrap px-3 py-2.5">
                          <PriorityBadge priority={row.priority} />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {drilldown.total > drilldown.rows.length && (
              <p className="mx-5 mt-3 text-xs text-gray-400">
                Read from the {drilldown.rows.length} oldest of {drilldown.total} tasks, so a filter
                can show fewer than its chip. Narrow the window to see the rest.
              </p>
            )}
          </div>
        )}
      </TaskDrawer>
    </div>
  );
};

export default TasksTab;
